import type { PaymentSuccess } from "../lib/gboModels";
import type { OnlinePaymentRequest, TemporaryCard } from "../features/checkout/payment";
import { normalizeCustomerAccountCode, normalizeInvoiceReferenceCode } from "../lib/lookupCode";
import { accountApi } from "./accountApi";
import { gboApi } from "./gboApiBase";
import { invoiceApi } from "./invoiceApi";

export type TemporaryCardMutationArg = {
  request: {
    customerId: string;
    token: string;
    zipCode?: string;
  };
};

export type PaymentScope =
  { type: "invoice"; invoiceCode: string } | { type: "account"; customerCode: string };

export type MakePaymentMutationArg = {
  request: OnlinePaymentRequest;
  scope?: PaymentScope;
};

export const paymentSubmissionApi = gboApi.injectEndpoints({
  endpoints: (builder) => ({
    createTemporaryCard: builder.mutation<TemporaryCard, TemporaryCardMutationArg>({
      query: ({ request }) => ({ url: "temp-card", method: "POST", body: request }),
    }),
    makePayment: builder.mutation<PaymentSuccess, MakePaymentMutationArg>({
      query: ({ request }) => ({ url: "payments", method: "POST", body: request }),
      async onQueryStarted({ scope }, { dispatch, queryFulfilled }) {
        let success: PaymentSuccess;
        try {
          success = (await queryFulfilled).data;
        } catch {
          return;
        }
        if (scope?.type === "invoice") {
          dispatch(
            invoiceApi.util.updateQueryData(
              "getInvoice",
              { invoiceCode: normalizeInvoiceReferenceCode(scope.invoiceCode) },
              (draft) => {
                draft.invoice = success.invoice ?? draft.invoice;
                draft.accountBalance = success.accountBalance;
              },
            ),
          );
        }

        if (scope?.type === "account") {
          dispatch(
            accountApi.util.updateQueryData(
              "getAccount",
              { customerCode: normalizeCustomerAccountCode(scope.customerCode) },
              (draft) => {
                draft.accountBalance = success.accountBalance;
              },
            ),
          );
        }
      },
    }),
  }),
});

export const { useCreateTemporaryCardMutation, useMakePaymentMutation } = paymentSubmissionApi;
